import { supabase } from '@/integrations/supabase/client';
import type { ActionResult, SingleResult } from '@/lib/rpc/types';

export type NotificationPreferences = {
  id: string;
  customer_id: string;
  email_enabled: boolean;
  push_enabled: boolean;
  sms_enabled: boolean;
  in_app_enabled: boolean;
  marketing: boolean;
  security_alerts: boolean;
  transaction_updates: boolean;
  digest_frequency: string; // Allow any string for compatibility
  updated_at: string;
};

/**
 * Get notification preferences via wallet domain RPC
 */
export async function getNotificationPreferences(): Promise<SingleResult<NotificationPreferences>> {
  try {
    // Use query string for GET request, as body may not be sent with GET
    const { data, error } = await supabase.functions.invoke('wallet?action=user.notificationPreferences', {
      method: 'GET'
    });

    if (error) {
      console.error('[Notifications] Wallet RPC error:', error);
      return { ok: false, error: error.message || 'Failed to get notification preferences' };
    }

    if (!data) {
      // Return default preferences if none found
      return {
        ok: true,
        data: {
          id: '',
          customer_id: '',
          email_enabled: true,
          push_enabled: false,
          sms_enabled: false,
          in_app_enabled: true,
          marketing: false,
          security_alerts: true,
          transaction_updates: true,
          digest_frequency: 'immediate',
          updated_at: new Date().toISOString(),
        } as NotificationPreferences
      };
    }

    return { ok: true, data: data as NotificationPreferences };
  } catch (error) {
    console.error('[Notifications] Exception:', error);
    return { ok: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Update notification preferences via wallet domain RPC
 */
export async function updateNotificationPreferences(preferences: Partial<NotificationPreferences>): Promise<ActionResult> {
  try {
    const { data, error } = await supabase.functions.invoke('wallet', {
      method: 'POST',
      body: {
        action: 'user.notificationPreferences',
        ...preferences
      }
    });

    if (error) {
      console.error('[Notifications] Update Wallet RPC error:', error);
      return { ok: false, error: error.message || 'Failed to update notification preferences' };
    }

    return { ok: true, data: data || null };
  } catch (error) {
    console.error('[Notifications] Update exception:', error);
    return { ok: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}